/**
 * The one place a figure's precision and grouping are decided.
 *
 * Engines return measures at full float precision, and the analyst is told to
 * repeat numbers exactly as returned, so 37.931034482758 reached the screen in
 * tables, tiles and prose alike. Every surface formats through here instead:
 * whole numbers as they are, anything else to two decimals, grouped the same
 * way on every machine so a figure reads identically in the app, an export and
 * an email. The API's presentation module applies the same rule to documents.
 */

export const DISPLAY_DECIMALS = 2;

/**
 * Fixed rather than the browser's locale: an en-IN machine groups 68675399 as
 * 6,86,75,399, which would differ from the same figure in the PDF.
 */
export const DISPLAY_LOCALE = "en-US";

const SMALL_SIGNIFICANT_DIGITS = 2;

const grouped = new Intl.NumberFormat(DISPLAY_LOCALE, { maximumFractionDigits: 0 });
const fixed = new Intl.NumberFormat(DISPLAY_LOCALE, {
  minimumFractionDigits: DISPLAY_DECIMALS,
  maximumFractionDigits: DISPLAY_DECIMALS,
});
const significant = new Intl.NumberFormat(DISPLAY_LOCALE, {
  maximumSignificantDigits: SMALL_SIGNIFICANT_DIGITS,
});

/**
 * A number as a reader should see it.
 *
 * A non-zero value too small to survive two decimals keeps two significant
 * digits instead, so 0.0034 is not reported as 0.00.
 */
export function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  if (Number.isInteger(value)) return grouped.format(value);
  const threshold = 0.5 * 10 ** -DISPLAY_DECIMALS;
  if (Math.abs(value) < threshold) return significant.format(value);
  return fixed.format(value);
}

/**
 * A number written with more than two decimals, as a standalone token.
 *
 * Bounded so identifiers survive: a version such as 1.0.0, a dotted member
 * name, or digits inside a word are not numbers a reader quantifies.
 */
const OVER_PRECISE =
  /(?<![\w.,])-?(?:\d{1,3}(?:,\d{3})+|\d+)\.\d{3,}(?![\w.]|,\d)/g;

/**
 * Rounds the over-precise numbers in free text, leaving everything else as
 * written. Used where figures arrive inside prose or Markdown table cells
 * rather than as typed values.
 */
export function roundNumbersInText(text: string): string {
  if (!text) return text;
  return text.replace(OVER_PRECISE, (token) => {
    const parsed = Number(token.replace(/,/g, ""));
    return Number.isFinite(parsed) ? formatNumber(parsed) : token;
  });
}
